import { Controller } from '@hotwired/stimulus';
import { getNextModalZIndex, hasOpenModals, registerModal, unregisterModal } from '../utilities/modal';
import { getBody } from '../utilities/browser';
import { onPropertyTransitionEndOnce } from '../utilities/events';

export default class extends Controller {
    static values = {
        name: String,
        backdropClose: {
            type: Boolean,
            default: true,
        },
        removeOnClose: Boolean,
        autoOpen: {
            type: Boolean,
            default: false,
        },
        delay: {
          type: Number,
          default: 0,
        },
        hasDynamicContent: Boolean,
        popupVersion: String,
        interval: String,
    };

    static targets = ['content', 'iframe'];

    get isOpen() {
        return this.element.classList.contains('open');
    }

    get modalId() {
        return this.element.getAttribute('id');
    }

    get openedByUrl() {
        return this._openedByUrl;
    }

    initialize() {
        this._openedByUrl = null;

        this.handleGlobalModalOpen = this.handleGlobalModalOpen.bind(this);
    }

    connect() {
        if (this.element.classList.contains('modal')) {
            registerModal(this);

            window.addEventListener('modal:open', this.handleGlobalModalOpen);

            if (this.autoOpenValue) {
                this.scheduleAutoOpen();
            }
        }
    }

    scheduleAutoOpen() {
        const delay = this.delayValue || 2000;
        
        setTimeout(() => {
            if (this.hasIntervalValue) {
                this.checkInterval();
            } else if (this.hasPopupVersionValue) {
                this.checkPopupVersion();
            } else {
                this.open();
            }
        }, delay);
    }

    checkPopupVersion() {
        const lastSeenVersion = localStorage.getItem(`${this.nameValue}PopupVersion`);

        if (!lastSeenVersion || parseFloat(this.popupVersionValue) > parseFloat(lastSeenVersion)) {
            setTimeout(() => {
                this.open();
            }, 1000);
        }
    }

    checkInterval() {
        const lastSeenTime = localStorage.getItem(`${this.nameValue}LastSeen`);
        const currentTime = Date.now();

        const intervalMs = parseInt(this.intervalValue) * 1000 * 60 * 60;
        const timeDifference = currentTime - parseInt(lastSeenTime);

        if (!lastSeenTime || timeDifference >= intervalMs) {
            setTimeout(() => {
                this.open();
            }, 1000);
        }
    }

    disconnect() {
        window.removeEventListener('modal:open', this.handleGlobalModalOpen);

        unregisterModal(this);
    }

    open() {
        if (this.isOpen) {
            return;
        }

        this.element.style.zIndex = getNextModalZIndex();
        getBody().classList.add('modal-active');

        this.element.classList.add('modal-in');
        requestAnimationFrame(() => {
            // Reset scroll to top, does not work on close when the modal is hidden
            this.element.scrollTop = 0;

            this.element.classList.add('open');

            if (this.hasDynamicContentValue) {
                this.loadDynamicContent();
            }
        });
    }

    handleGlobalModalOpen(event) {
        const detail = event?.detail;
        if (!detail || typeof detail !== 'object') {
            return;
        }

        const byName = detail.name && detail.name === this.nameValue;
        const byId = (detail.id && detail.id === this.modalId) || (detail.modalId && detail.modalId === this.modalId);

        if (!byName && !byId) {
            return;
        }

        this.open();
    }

    loadDynamicContent() {
        if (this.hasIframeTarget && this.hasContentTarget) {
            this.iframeTarget.srcdoc = this.contentTarget.innerHTML;
        }
    }

    /**
     * Confirmation modals: emits "modal:confirm" (listened to in actions.js) and closes
     */
    confirm() {
        this.dispatch('confirm');
        this.close();
    }

    closeAndReload() {
        this.close();

        location.reload();
    }

    close() {
        if (!this.element || !this.isOpen) {
            return;
        }

        if (this.hasIntervalValue) {
            localStorage.setItem(`${this.nameValue}LastSeen`, Date.now().toString());
        }
        else if (this.hasPopupVersionValue) {
            localStorage.setItem(`${this.nameValue}PopupVersion`, this.popupVersionValue);
        }

        onPropertyTransitionEndOnce(this.element, 'opacity', () => {
            this.element.classList.remove('modal-in');

            if (!hasOpenModals()) {
                getBody().classList.remove('modal-active');
            }

            if (this.removeOnCloseValue) {
                this.element.remove();
            } else {
                this.reset();
            }
        });
        this.element.classList.remove('open');

        this.dispatch('close');
    }

    reset() {
        this.element.querySelectorAll('form').forEach((form) => {
            form.reset();
        });
    }

    get hasPopupVersionValue() {
        return this.popupVersionValue !== undefined;
    }

    get hasIntervalValue() {
        return this.intervalValue !== undefined && this.intervalValue !== '';
    }
}
